import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Import Job Types ---

export interface ImportFieldMapping {
  source_column: string;
  target_field: string;
  default_value: string | null;
  is_required: boolean;
}

export interface ImportRowError {
  row_number: number;
  field: string;
  value: string | null;
  error: string;
}

export interface ImportJob {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  target_module: string;
  file_format: string;
  file_name: string;
  file_size: number;
  file_id: string;
  status: string;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  duplicate_rows: number;
  field_mappings: ImportFieldMapping[];
  duplicate_strategy: string;
  errors: ImportRowError[];
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  owner_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateImportJobRequest {
  name: string;
  description?: string;
  target_module: string;
  file_format: string;
  file_name: string;
  file_size: number;
  file_id: string;
  field_mappings?: ImportFieldMapping[];
  duplicate_strategy?: string;
}

export interface UpdateImportJobRequest {
  name?: string;
  description?: string;
  field_mappings?: ImportFieldMapping[];
  duplicate_strategy?: string;
}

// --- Export Job Types ---

export interface ExportJob {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  source_module: string;
  file_format: string;
  status: string;
  columns: string[];
  filters: Record<string, string>;
  sort_field: string | null;
  sort_descending: boolean;
  total_rows: number;
  exported_rows: number;
  file_name: string | null;
  file_size: number;
  file_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  error_message: string | null;
  owner_id: string;
  include_headers: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateExportJobRequest {
  name: string;
  description?: string;
  source_module: string;
  file_format: string;
  columns?: string[];
  filters?: Record<string, string>;
  sort_field?: string;
  sort_descending?: boolean;
  include_headers?: boolean;
}

export interface UpdateExportJobRequest {
  name?: string;
  description?: string;
  columns?: string[];
  filters?: Record<string, string>;
  sort_field?: string;
  sort_descending?: boolean;
  include_headers?: boolean;
}

// --- Constants ---

export const ImportJobStatuses = ['pending', 'validating', 'validated', 'importing', 'completed', 'failed', 'cancelled'] as const;
export const ExportJobStatuses = ['pending', 'exporting', 'completed', 'failed', 'cancelled'] as const;
export const ImportFileFormats = ['csv', 'json', 'xlsx'] as const;
export const ImportDuplicateStrategies = ['skip', 'overwrite', 'create_new'] as const;
export const DataManagementModules = ['customers', 'contacts', 'opportunities', 'leads', 'products', 'activities', 'tickets', 'orders'] as const;

// --- Import Job API ---

export const listImportJobs = (params: SearchParams & {
  status?: string;
  target_module?: string;
  owner_id?: string;
}) =>
  apiGet<ApiResponse<PagedResponse<ImportJob>>>('/import-jobs', params);

export const getImportJob = (id: string) =>
  apiGet<ApiResponse<ImportJob>>(`/import-jobs/${id}`);

export const createImportJob = (data: CreateImportJobRequest) =>
  apiPost<ApiResponse<ImportJob>>('/import-jobs', data);

export const updateImportJob = (id: string, data: UpdateImportJobRequest) =>
  apiPut<ApiResponse<ImportJob>>(`/import-jobs/${id}`, data);

export const deleteImportJob = (id: string) =>
  apiDelete<ApiResponse<object>>(`/import-jobs/${id}`);

export const validateImportJob = (id: string) =>
  apiPost<ApiResponse<ImportJob>>(`/import-jobs/${id}/validate`);

export const executeImportJob = (id: string) =>
  apiPost<ApiResponse<ImportJob>>(`/import-jobs/${id}/execute`);

export const cancelImportJob = (id: string) =>
  apiPost<ApiResponse<ImportJob>>(`/import-jobs/${id}/cancel`);

export const getImportTargetFields = (module: string) =>
  apiGet<ApiResponse<string[]>>(`/import-jobs/target-fields/${module}`);

// --- Export Job API ---

export const listExportJobs = (params: SearchParams & {
  status?: string;
  source_module?: string;
  owner_id?: string;
}) =>
  apiGet<ApiResponse<PagedResponse<ExportJob>>>('/export-jobs', params);

export const getExportJob = (id: string) =>
  apiGet<ApiResponse<ExportJob>>(`/export-jobs/${id}`);

export const createExportJob = (data: CreateExportJobRequest) =>
  apiPost<ApiResponse<ExportJob>>('/export-jobs', data);

export const updateExportJob = (id: string, data: UpdateExportJobRequest) =>
  apiPut<ApiResponse<ExportJob>>(`/export-jobs/${id}`, data);

export const deleteExportJob = (id: string) =>
  apiDelete<ApiResponse<object>>(`/export-jobs/${id}`);

export const executeExportJob = (id: string) =>
  apiPost<ApiResponse<ExportJob>>(`/export-jobs/${id}/execute`);

export const cancelExportJob = (id: string) =>
  apiPost<ApiResponse<ExportJob>>(`/export-jobs/${id}/cancel`);

export const getExportAvailableColumns = (module: string) =>
  apiGet<ApiResponse<string[]>>(`/export-jobs/available-columns/${module}`);
